import type { ContextCompaction } from "@/agents-v2/chat-runtime/summarization-stream";
import type { AttachmentAdapter } from "@assistant-ui/react";
import type {
  LangChainMessage,
  LangGraphSendMessageConfig
} from "@assistant-ui/react-langgraph";
import type { Client, Metadata } from "@langchain/langgraph-sdk";
import type { ReactNode } from "react";

export type LibraryChatSelectedFile = {
  unique_id: string;
  filename: string;
  folder: string;
  isFolder?: boolean;
};

export type AgentMessageFeedback = {
  type: "positive" | "negative";
  updated_at: string;
};

export type AgentThreadMetadata = NonNullable<Metadata> & {
  assistant_id?: string;
  feedback?: Record<string, AgentMessageFeedback>;
  graph_id?: string;
  bid_id?: string;
  /** Origin section id, stamped once when the thread is created. */
  section_id?: string;
  /** Origin section number snapshot (e.g. "Q1"), for permanent history labels. */
  section_number?: string;
  /** True on the thread that built the bid (agentic creation). */
  creation?: boolean | string;
  hidden?: boolean | string;
  title?: string;
  archived?: boolean | string;
  thread_name?: string;
  owner?: string;
  owner_email?: string;
  selected_tools?: string[];
  selected_folders?: string[];
  selected_files?: string[];
  selected_file_items?: LibraryChatSelectedFile[];
  ui?: Record<string, unknown>;
  /** Where the thread was started: the Word pane stamps "word". */
  surface?: string;
  /** The Word document the thread belongs to (see taskpane/document/identity). */
  document_id?: string;
};

export type ChatProviderProps = {
  assistantId: string;
  attachmentAdapter?: AttachmentAdapter;
  context?: unknown;
  resolveContext?: () => unknown;
  onBeforeSend?: () => Promise<void> | void;
  onStreamComplete?: () => Promise<void> | void;
  initialMetadata?: AgentThreadMetadata;
  threadScopeMetadata?: AgentThreadMetadata;
  prepareMessages?: (
    threadId: string,
    messages: LangChainMessage[]
  ) => LangChainMessage[];
  token: string;
  children: ReactNode;
};

export type AgentsChatContextValue = {
  client: Client;
  currentThreadId: string | null;
  currentMetadata: AgentThreadMetadata;
  refreshCurrentThread: () => Promise<void>;
  updateCurrentThreadMetadata: (patch: ThreadMetadataPatch) => Promise<void>;
  cancelActiveRun: () => Promise<void>;
  /** Corrupted thread that can't continue; null when healthy. */
  corruptedThreadId: string | null;
  /** Clears the corrupted flag. */
  clearCorruptedThread: () => void;
  /** The session expired mid-run and could not be renewed. */
  sessionExpired: boolean;
  /** The agent is compacting older turns of the thread (PE-626). */
  isContextCompacting: boolean;
  /** Where and what the agent compacted in the current thread, if anything. */
  contextCompaction: ContextCompaction | null;
};

export type ThreadMetadataPatch =
  | AgentThreadMetadata
  | ((current: AgentThreadMetadata) => AgentThreadMetadata);

export type ThreadMetadataPatcher = (
  threadId: string,
  patch: ThreadMetadataPatch
) => Promise<AgentThreadMetadata>;

export type LangGraphSendExtras = {
  send: (
    msgs: LangChainMessage[],
    cfg: LangGraphSendMessageConfig
  ) => Promise<void>;
};

export type AgentThreadValues = {
  messages?: LangChainMessage[];
  skills_metadata?: unknown[];
};

export type LangGraphMessageState = AgentThreadValues;

export type AgentThreadSummary = {
  thread_id: string;
  status?: string;
  metadata?: Metadata;
  user_id?: string | null;
  created_at?: string;
  updated_at?: string;
};

export type StreamPayload = NonNullable<
  Parameters<Client["runs"]["stream"]>[2]
>;

export const STREAM_MODE: NonNullable<StreamPayload["streamMode"]> = [
  "messages",
  "updates",
  "custom"
];
